/**
 * Writing one group's board: a Markdown field and a preview of what members
 * will see, on two tabs.
 *
 * Opened from the boards page and from a board on the dashboard. Emptying the
 * field removes the board, and the button says so.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BOARD_LIMITS, type BoardDto } from '@forsys/shared';
import { FormDialog } from '../../components/FormDialog';
import { MarkdownText } from '../../components/MarkdownText';
import { useSubmit } from '../../hooks/useSubmit';
import { setBoard } from './api';

export interface BoardDialogProps {
  /** The board to edit. Null: closed. */
  board: BoardDto | null;
  onClose: () => void;
  onSaved: (board: BoardDto) => void;
}

export function BoardDialog({ board, onClose, onSaved }: BoardDialogProps) {
  const { t } = useTranslation('board');
  const [draft, setDraft] = useState('');
  const [tab, setTab] = useState<'write' | 'preview'>('write');

  const save = useSubmit(async (groupId: number, text: string) => (await setBoard(groupId, text)).board);
  const { clearError } = save;

  // Each time it opens, start from the board as it stands.
  useEffect(() => {
    if (!board) return;
    setDraft(board.text);
    setTab('write');
    clearError();
  }, [board, clearError]);

  const removing = draft.trim() === '' && board !== null && board.text !== '';
  const tooLong = draft.length > BOARD_LIMITS.textLength;

  return (
    <FormDialog
      open={board !== null}
      title={t('dialog.title', { group: board?.groupName ?? '' })}
      submitLabel={removing ? t('dialog.remove') : undefined}
      pending={save.pending}
      error={save.error}
      disabled={tooLong || draft === board?.text || (draft.trim() === '' && board?.text === '')}
      maxWidth="md"
      onSubmit={() => {
        if (!board) return;
        void save.submit(board.groupId, draft).then((saved) => {
          if (saved) onSaved(saved);
        });
      }}
      onClose={onClose}
    >
      <Tabs value={tab} onChange={(_event, value: 'write' | 'preview') => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="write" label={t('dialog.write')} />
        <Tab value="preview" label={t('dialog.preview')} disabled={draft.trim() === ''} />
      </Tabs>

      {tab === 'write' ? (
        <TextField
          autoFocus
          multiline
          minRows={8}
          maxRows={20}
          label={t('dialog.label')}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          error={tooLong}
          helperText={
            tooLong
              ? t('dialog.tooLong', { max: BOARD_LIMITS.textLength, length: draft.length })
              : t('dialog.help')
          }
          slotProps={{ htmlInput: { spellCheck: true } }}
          fullWidth
        />
      ) : (
        <Box sx={{ minHeight: 200, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <MarkdownText>{draft}</MarkdownText>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('dialog.visibility')}
      </Typography>
    </FormDialog>
  );
}
